export interface Project {
  id: string;
  name: string;
  description: string;
  status: "active" | "paused" | "completed" | "cancelled";
  stage: "kickoff" | "design" | "development" | "testing" | "launch";
  stageIndex: number;
  client: string;
  clientId: string;
  startDate: string;
  dueDate: string;
  progress: number;
  tech: string[];
  updates: ProjectUpdate[];
  team: string[];
}

export interface ProjectUpdate {
  id: string;
  author: string;
  role: string;
  message: string;
  createdAt: string;
}

export interface Ticket {
  id: string;
  subject: string;
  message: string;
  status: "open" | "in_progress" | "resolved" | "closed";
  priority: "low" | "medium" | "high" | "urgent";
  projectId?: string;
  projectName?: string;
  createdAt: string;
  replies: TicketReply[];
}

export interface TicketReply {
  id: string;
  author: string;
  role: string;
  message: string;
  createdAt: string;
}

export interface Invoice {
  id: string;
  number: string;
  amount: number;
  status: "draft" | "sent" | "paid" | "overdue";
  dueDate: string;
  paidAt?: string;
  description: string;
  items: { label: string; amount: number }[];
}

export interface Subscription {
  id: string;
  plan: "starter" | "growth" | "enterprise";
  status: "active" | "cancelled" | "past_due";
  amount: number;
  billingCycle: "monthly" | "annual";
  currentPeriodEnd: string;
  stripeSubId: string;
}

export const STAGES = ["kickoff", "design", "development", "testing", "launch"] as const;
export const STAGE_LABELS: Record<string, string> = {
  kickoff: "Kickoff",
  design: "Design",
  development: "Development",
  testing: "Testing",
  launch: "Launch",
};

export const mockProjects: Project[] = [
  {
    id: "p1",
    name: "NexaPay — Fintech Platform",
    description: "Full-stack payment processing platform with multi-currency support, KYC verification, and real-time transaction monitoring.",
    status: "active",
    stage: "development",
    stageIndex: 2,
    client: "NexaPay Inc.",
    clientId: "c1",
    startDate: "2025-01-15",
    dueDate: "2025-07-15",
    progress: 62,
    tech: ["Node.js", "React", "PostgreSQL", "Stripe", "Redis"],
    team: ["Alex Johnson", "Sarah Chen", "Marcus Williams"],
    updates: [
      { id: "upd1", author: "Alex Johnson", role: "Lead Engineer", message: "Payment gateway integration complete. Moving to KYC module next week.", createdAt: new Date(Date.now() - 86400000).toISOString() },
      { id: "upd2", author: "Sarah Chen", role: "Project Manager", message: "Design review approved by client. Development sprint 4 starting Monday.", createdAt: new Date(Date.now() - 172800000).toISOString() },
    ],
  },
  {
    id: "p2",
    name: "TradeCore — Algo Trading System",
    description: "High-frequency algorithmic trading platform with real-time market data feeds and backtesting engine.",
    status: "active",
    stage: "testing",
    stageIndex: 3,
    client: "TradeCore Capital",
    clientId: "c2",
    startDate: "2024-10-01",
    dueDate: "2025-06-30",
    progress: 85,
    tech: ["Python", "WebSocket", "TimescaleDB", "React", "Docker"],
    team: ["Marcus Williams", "Alex Johnson"],
    updates: [
      { id: "upd3", author: "Marcus Williams", role: "Backend Engineer", message: "Load testing complete. System handles 10,000 concurrent orders with <45ms latency.", createdAt: new Date(Date.now() - 3600000).toISOString() },
    ],
  },
  {
    id: "p3",
    name: "Synapse AI — LLM Agent Platform",
    description: "Enterprise AI agent orchestration platform with full observability and multi-model support.",
    status: "completed",
    stage: "launch",
    stageIndex: 4,
    client: "Synapse Labs",
    clientId: "c3",
    startDate: "2024-06-01",
    dueDate: "2024-11-30",
    progress: 100,
    tech: ["Python", "LangChain", "OpenAI", "React", "FastAPI"],
    team: ["Alex Johnson", "Sarah Chen"],
    updates: [
      { id: "upd4", author: "Alex Johnson", role: "Lead Engineer", message: "Project successfully launched. All 1,200+ agents deployed and running.", createdAt: new Date(Date.now() - 604800000).toISOString() },
    ],
  },
];

export const mockTickets: Ticket[] = [
  {
    id: "t1",
    subject: "API rate limiting issue on payment endpoint",
    message: "We're seeing 429 errors on the /api/v1/payments endpoint during peak hours (9am–11am UTC). This is affecting our transaction processing and causing failed payments for end users. We need this resolved urgently.",
    status: "in_progress",
    priority: "high",
    projectId: "p1",
    projectName: "NexaPay — Fintech Platform",
    createdAt: new Date(Date.now() - 86400000).toISOString(),
    replies: [
      { id: "r1", author: "Marcus Williams", role: "Support Engineer", message: "We've identified the issue. The rate limiter config was set too conservatively at 50 req/min. We're increasing the limit to 200 req/min for your account and deploying the fix now. ETA: 30 minutes.", createdAt: new Date(Date.now() - 43200000).toISOString() },
      { id: "r2", author: "David Okafor", role: "Client", message: "Thanks for the quick response. We're still seeing some 429s — has the deployment gone through yet?", createdAt: new Date(Date.now() - 21600000).toISOString() },
      { id: "r3", author: "Marcus Williams", role: "Support Engineer", message: "Deployment is live. The new limit is active. Please monitor over the next hour and let us know if you see any further issues.", createdAt: new Date(Date.now() - 10800000).toISOString() },
    ],
  },
  {
    id: "t2",
    subject: "Request: Add CSV export to transaction history",
    message: "Our finance team needs to export transaction data to CSV for monthly reconciliation. The current dashboard only shows 30 days of data and there's no export option. Can this be added to the dashboard? Ideally with date range filtering.",
    status: "open",
    priority: "medium",
    projectId: "p1",
    projectName: "NexaPay — Fintech Platform",
    createdAt: new Date(Date.now() - 172800000).toISOString(),
    replies: [],
  },
  {
    id: "t3",
    subject: "Backtesting engine returning incorrect P&L calculations",
    message: "The backtesting results for our momentum strategy show a 12% discrepancy compared to our manual calculations. We've run the same strategy on 3 different date ranges and all show the same offset. This is a critical issue before we go live.",
    status: "resolved",
    priority: "urgent",
    projectId: "p2",
    projectName: "TradeCore — Algo Trading System",
    createdAt: new Date(Date.now() - 604800000).toISOString(),
    replies: [
      { id: "r4", author: "Alex Johnson", role: "Lead Engineer", message: "Investigating now. Can you share the strategy config and the date range you tested? That will help us reproduce the issue faster.", createdAt: new Date(Date.now() - 590400000).toISOString() },
      { id: "r5", author: "Emma Larsson", role: "Client", message: "Config attached in the ticket notes. Date range: Jan 1 – Mar 31 2024. Strategy: BTC/USDT momentum with 4h candles.", createdAt: new Date(Date.now() - 576000000).toISOString() },
      { id: "r6", author: "Alex Johnson", role: "Lead Engineer", message: "Found the bug — slippage calculation was using bid price instead of mid price. Fixed and redeployed to staging. Please verify.", createdAt: new Date(Date.now() - 518400000).toISOString() },
      { id: "r7", author: "Emma Larsson", role: "Client", message: "Confirmed — P&L now matches within 0.1% of our manual calculations. Great work!", createdAt: new Date(Date.now() - 432000000).toISOString() },
      { id: "r8", author: "Sarah Chen", role: "Project Manager", message: "Marking as resolved. We've also added a regression test to prevent this from recurring. Thanks for catching it!", createdAt: new Date(Date.now() - 400000000).toISOString() },
    ],
  },
  {
    id: "t4",
    subject: "WebSocket connection drops after 5 minutes of inactivity",
    message: "The real-time market data feed disconnects after approximately 5 minutes of no new data. Our trading bots rely on this connection being persistent. When it drops, we miss price updates and orders fail to execute.",
    status: "open",
    priority: "urgent",
    projectId: "p2",
    projectName: "TradeCore — Algo Trading System",
    createdAt: new Date(Date.now() - 7200000).toISOString(),
    replies: [
      { id: "r9", author: "Marcus Williams", role: "Support Engineer", message: "This is a known issue with the load balancer's idle timeout. We're implementing a heartbeat ping every 30 seconds to keep the connection alive. Fix will be in the next deployment.", createdAt: new Date(Date.now() - 3600000).toISOString() },
    ],
  },
  {
    id: "t5",
    subject: "AI agent not respecting system prompt constraints",
    message: "The customer service agent we deployed is occasionally ignoring the persona constraints in the system prompt and responding in a generic way. This happens roughly 1 in 20 conversations. We need it to stay in character 100% of the time.",
    status: "in_progress",
    priority: "high",
    projectId: "p3",
    projectName: "Synapse AI — LLM Agent Platform",
    createdAt: new Date(Date.now() - 259200000).toISOString(),
    replies: [
      { id: "r10", author: "Alex Johnson", role: "Lead Engineer", message: "We've reviewed the conversation logs. The issue occurs when the user input contains certain adversarial patterns that override the system prompt. We're implementing a prompt injection guard layer.", createdAt: new Date(Date.now() - 216000000).toISOString() },
      { id: "r11", author: "Kwame Asante", role: "Client", message: "Good to know. Can you also add a fallback response for when the guard triggers? Something like 'I can only help with X' rather than a generic error.", createdAt: new Date(Date.now() - 172800000).toISOString() },
      { id: "r12", author: "Alex Johnson", role: "Lead Engineer", message: "Absolutely. We'll add a configurable fallback message. You'll be able to set it from the agent dashboard. Deploying the guard layer tomorrow.", createdAt: new Date(Date.now() - 86400000).toISOString() },
    ],
  },
  {
    id: "t6",
    subject: "Invoice PDF generation failing for amounts over $10,000",
    message: "When we try to generate a PDF invoice for amounts over $10,000, the system throws a 500 error. Smaller invoices work fine. This is blocking our billing cycle for this month.",
    status: "resolved",
    priority: "high",
    projectId: "p1",
    projectName: "NexaPay — Fintech Platform",
    createdAt: new Date(Date.now() - 1209600000).toISOString(),
    replies: [
      { id: "r13", author: "Sarah Chen", role: "Project Manager", message: "Confirmed the bug. The PDF renderer was using an integer field that overflowed at 9,999. Fixed to use a decimal type. Deployed to production.", createdAt: new Date(Date.now() - 1123200000).toISOString() },
      { id: "r14", author: "David Okafor", role: "Client", message: "Working perfectly now. Thanks for the fast fix!", createdAt: new Date(Date.now() - 1036800000).toISOString() },
    ],
  },
  {
    id: "t7",
    subject: "Request: Dark mode for the client-facing dashboard",
    message: "Several of our users have requested a dark mode option for the dashboard. Our platform is used heavily at night by traders and a dark theme would significantly improve usability. Is this something that can be added?",
    status: "open",
    priority: "low",
    projectId: "p2",
    projectName: "TradeCore — Algo Trading System",
    createdAt: new Date(Date.now() - 345600000).toISOString(),
    replies: [],
  },
  {
    id: "t8",
    subject: "KYC verification stuck in 'pending' state for 48+ hours",
    message: "We have 3 users whose KYC verification has been stuck in 'pending' for over 48 hours. The Onfido webhook should have updated their status but it hasn't. User IDs: USR-4421, USR-4438, USR-4451.",
    status: "in_progress",
    priority: "urgent",
    projectId: "p1",
    projectName: "NexaPay — Fintech Platform",
    createdAt: new Date(Date.now() - 172800000).toISOString(),
    replies: [
      { id: "r15", author: "Marcus Williams", role: "Support Engineer", message: "Checked the webhook logs — Onfido sent the callbacks but our endpoint returned a 200 without processing them due to a signature mismatch. We've fixed the signature validation and manually re-triggered the webhooks for those 3 users.", createdAt: new Date(Date.now() - 129600000).toISOString() },
      { id: "r16", author: "David Okafor", role: "Client", message: "USR-4421 and USR-4438 are now verified. USR-4451 is still pending.", createdAt: new Date(Date.now() - 86400000).toISOString() },
      { id: "r17", author: "Marcus Williams", role: "Support Engineer", message: "USR-4451 had a different issue — their document scan failed Onfido's liveness check. We've sent them an email to re-submit. Should be resolved within 24 hours.", createdAt: new Date(Date.now() - 43200000).toISOString() },
    ],
  },
  {
    id: "t9",
    subject: "Mobile app crashes on iOS 17.4 when opening notifications",
    message: "After the iOS 17.4 update, our app crashes immediately when a user taps on a push notification. The crash log points to a null pointer in the notification handler. This is affecting all iOS users on 17.4.",
    status: "open",
    priority: "high",
    projectId: "p3",
    projectName: "Synapse AI — LLM Agent Platform",
    createdAt: new Date(Date.now() - 3600000).toISOString(),
    replies: [],
  },
  {
    id: "t10",
    subject: "Request: Multi-language support for agent responses",
    message: "We're expanding to French and Spanish markets. Can the AI agent be configured to detect the user's language and respond accordingly? We'd need French and Spanish to be supported initially.",
    status: "closed",
    priority: "medium",
    projectId: "p3",
    projectName: "Synapse AI — LLM Agent Platform",
    createdAt: new Date(Date.now() - 2592000000).toISOString(),
    replies: [
      { id: "r18", author: "Alex Johnson", role: "Lead Engineer", message: "This is fully supported by the underlying LLM. We've added a language detection step to the agent pipeline and configured French and Spanish response modes. Live in production.", createdAt: new Date(Date.now() - 2505600000).toISOString() },
      { id: "r19", author: "Kwame Asante", role: "Client", message: "Tested and working great. Closing this ticket.", createdAt: new Date(Date.now() - 2419200000).toISOString() },
    ],
  },
  {
    id: "t11",
    subject: "Database query performance degradation after migration",
    message: "Since the PostgreSQL migration last week, several dashboard queries are taking 8–12 seconds instead of under 1 second. The affected queries are the transaction summary and the user analytics endpoints.",
    status: "resolved",
    priority: "high",
    projectId: "p1",
    projectName: "NexaPay — Fintech Platform",
    createdAt: new Date(Date.now() - 864000000).toISOString(),
    replies: [
      { id: "r20", author: "Marcus Williams", role: "Support Engineer", message: "The migration didn't carry over the composite indexes on the transactions table. We've re-created them. Query times are back to under 200ms.", createdAt: new Date(Date.now() - 820800000).toISOString() },
      { id: "r21", author: "David Okafor", role: "Client", message: "Confirmed — dashboard is fast again. Thanks!", createdAt: new Date(Date.now() - 777600000).toISOString() },
    ],
  },
  {
    id: "t12",
    subject: "Billing portal shows incorrect subscription renewal date",
    message: "The billing portal shows our subscription renewing on March 1st but we signed up on March 15th. This is causing confusion for our finance team. Please correct the renewal date.",
    status: "open",
    priority: "low",
    projectId: undefined,
    projectName: undefined,
    createdAt: new Date(Date.now() - 432000000).toISOString(),
    replies: [],
  },
];

export const mockInvoices: Invoice[] = [
  {
    id: "inv1",
    number: "INV-2025-042",
    amount: 4250,
    status: "sent",
    dueDate: new Date(Date.now() + 604800000).toISOString(),
    description: "NexaPay Development — Sprint 4",
    items: [
      { label: "Backend Development (80hrs)", amount: 3200 },
      { label: "UI/UX Design (20hrs)", amount: 800 },
      { label: "DevOps & Deployment", amount: 250 },
    ],
  },
  {
    id: "inv2",
    number: "INV-2025-038",
    amount: 8500,
    status: "paid",
    dueDate: new Date(Date.now() - 604800000).toISOString(),
    paidAt: new Date(Date.now() - 518400000).toISOString(),
    description: "NexaPay Development — Sprint 1-3",
    items: [
      { label: "Full Stack Development (200hrs)", amount: 8000 },
      { label: "Project Management", amount: 500 },
    ],
  },
  {
    id: "inv3",
    number: "INV-2025-031",
    amount: 2500,
    status: "paid",
    dueDate: new Date(Date.now() - 1209600000).toISOString(),
    paidAt: new Date(Date.now() - 1123200000).toISOString(),
    description: "Project Kickoff & Discovery",
    items: [
      { label: "Discovery & Architecture", amount: 2000 },
      { label: "Technical Specification", amount: 500 },
    ],
  },
];

export const mockSubscription: Subscription = {
  id: "sub1",
  plan: "growth",
  status: "active",
  amount: 149,
  billingCycle: "monthly",
  currentPeriodEnd: new Date(Date.now() + 1296000000).toISOString(),
  stripeSubId: "sub_mock_growth_001",
};

export const mockAdminStats = {
  totalClients: 47,
  activeProjects: 23,
  mrr: 12450,
  newLeads: 8,
  openTickets: 14,
  totalRevenue: 284500,
  clientGrowth: 12,
  projectGrowth: 8,
  mrrGrowth: 18,
  leadGrowth: 25,
};

export const mockRevenueData = [
  { month: "Jul", revenue: 18200, expenses: 6400, profit: 11800 },
  { month: "Aug", revenue: 21500, expenses: 7100, profit: 14400 },
  { month: "Sep", revenue: 19800, expenses: 6800, profit: 13000 },
  { month: "Oct", revenue: 24300, expenses: 8200, profit: 16100 },
  { month: "Nov", revenue: 22100, expenses: 7500, profit: 14600 },
  { month: "Dec", revenue: 28900, expenses: 9100, profit: 19800 },
  { month: "Jan", revenue: 26400, expenses: 8600, profit: 17800 },
  { month: "Feb", revenue: 31200, expenses: 9800, profit: 21400 },
  { month: "Mar", revenue: 29700, expenses: 9200, profit: 20500 },
  { month: "Apr", revenue: 34500, expenses: 10400, profit: 24100 },
  { month: "May", revenue: 38200, expenses: 11200, profit: 27000 },
  { month: "Jun", revenue: 42800, expenses: 12300, profit: 30500 },
];

export const mockTicketTrendData = [
  { week: "W1 Jan", opened: 12, resolved: 8, avgResolutionHrs: 18 },
  { week: "W2 Jan", opened: 9, resolved: 11, avgResolutionHrs: 14 },
  { week: "W3 Jan", opened: 15, resolved: 10, avgResolutionHrs: 22 },
  { week: "W4 Jan", opened: 7, resolved: 13, avgResolutionHrs: 11 },
  { week: "W1 Feb", opened: 11, resolved: 9, avgResolutionHrs: 16 },
  { week: "W2 Feb", opened: 14, resolved: 12, avgResolutionHrs: 19 },
  { week: "W3 Feb", opened: 8, resolved: 14, avgResolutionHrs: 9 },
  { week: "W4 Feb", opened: 10, resolved: 11, avgResolutionHrs: 13 },
  { week: "W1 Mar", opened: 16, resolved: 13, avgResolutionHrs: 21 },
  { week: "W2 Mar", opened: 6, resolved: 15, avgResolutionHrs: 8 },
  { week: "W3 Mar", opened: 13, resolved: 10, avgResolutionHrs: 17 },
  { week: "W4 Mar", opened: 9, resolved: 12, avgResolutionHrs: 12 },
];

export const mockProjectMetrics = [
  {
    id: "p1",
    name: "NexaPay",
    client: "NexaPay Inc.",
    progress: 62,
    status: "active",
    budget: 48000,
    spent: 29760,
    hoursLogged: 496,
    hoursTotal: 800,
    tasksCompleted: 34,
    tasksTotal: 55,
    dueDate: "2025-07-15",
    color: "#00D4FF",
  },
  {
    id: "p2",
    name: "TradeCore",
    client: "TradeCore Capital",
    progress: 85,
    status: "active",
    budget: 62000,
    spent: 52700,
    hoursLogged: 680,
    hoursTotal: 800,
    tasksCompleted: 51,
    tasksTotal: 60,
    dueDate: "2025-06-30",
    color: "#00FF88",
  },
  {
    id: "p3",
    name: "Synapse AI",
    client: "Synapse Labs",
    progress: 100,
    status: "completed",
    budget: 35000,
    spent: 33200,
    hoursLogged: 560,
    hoursTotal: 560,
    tasksCompleted: 42,
    tasksTotal: 42,
    dueDate: "2024-11-30",
    color: "#FF6B35",
  },
  {
    id: "p4",
    name: "FlowOps",
    client: "FlowOps Inc.",
    progress: 28,
    status: "active",
    budget: 22000,
    spent: 6160,
    hoursLogged: 112,
    hoursTotal: 400,
    tasksCompleted: 8,
    tasksTotal: 29,
    dueDate: "2025-09-01",
    color: "#A855F7",
  },
  {
    id: "p5",
    name: "MediTrack",
    client: "MediTrack Health",
    progress: 45,
    status: "active",
    budget: 55000,
    spent: 24750,
    hoursLogged: 330,
    hoursTotal: 740,
    tasksCompleted: 18,
    tasksTotal: 40,
    dueDate: "2025-08-20",
    color: "#F59E0B",
  },
];

export const mockKpiData = {
  avgProjectDuration: 5.2,
  onTimeDeliveryRate: 87,
  clientSatisfaction: 4.8,
  ticketResolutionRate: 94,
  avgTicketResolutionHrs: 14.3,
  revenuePerClient: 6053,
  utilizationRate: 82,
  repeatClientRate: 71,
};

export const mockClients = [
  { id: "c1", name: "NexaPay Inc.", contact: "David Okafor", email: "david@nexapay.com", plan: "growth", status: "active", projects: 2, mrr: 149, joinedAt: "2025-01-10" },
  { id: "c2", name: "TradeCore Capital", contact: "Emma Larsson", email: "emma@tradecore.io", plan: "enterprise", status: "active", projects: 1, mrr: 499, joinedAt: "2024-09-15" },
  { id: "c3", name: "Synapse Labs", contact: "Kwame Asante", email: "kwame@synapse.ai", plan: "growth", status: "active", projects: 1, mrr: 149, joinedAt: "2024-05-20" },
  { id: "c4", name: "FlowOps Inc.", contact: "Priya Sharma", email: "priya@flowops.com", plan: "starter", status: "active", projects: 1, mrr: 49, joinedAt: "2024-11-01" },
  { id: "c5", name: "MediTrack Health", contact: "James Osei", email: "james@meditrack.health", plan: "enterprise", status: "active", projects: 1, mrr: 499, joinedAt: "2024-03-12" },
  { id: "c6", name: "ShipFast Commerce", contact: "Amara Diallo", email: "amara@shipfast.co", plan: "growth", status: "paused", projects: 1, mrr: 0, joinedAt: "2023-08-05" },
];

export const mockLeads = [
  { id: "l1", name: "Chidi Nwosu", email: "chidi@techstartup.ng", phone: "+234 801 234 5678", company: "TechStartup NG", service: "SaaS Application", message: "We need a multi-tenant SaaS platform for our logistics business.", status: "new", createdAt: new Date(Date.now() - 3600000).toISOString(), assignedTo: null },
  { id: "l2", name: "Fatima Al-Hassan", email: "fatima@fintechco.ae", phone: "+971 50 123 4567", company: "FinTech Co UAE", service: "Fintech Integration", message: "Looking for payment gateway integration with local UAE banks.", status: "contacted", createdAt: new Date(Date.now() - 86400000).toISOString(), assignedTo: "Alex Johnson" },
  { id: "l3", name: "Tobias Müller", email: "tobias@automate.de", phone: "+49 30 1234 5678", company: "Automate GmbH", service: "Automation System", message: "Need to automate our invoice processing and ERP integration.", status: "proposal", createdAt: new Date(Date.now() - 172800000).toISOString(), assignedTo: "Sarah Chen" },
  { id: "l4", name: "Aisha Kamara", email: "aisha@aiventures.com", phone: "+1 415 555 0123", company: "AI Ventures", service: "AI Tools", message: "Building an AI-powered customer service agent for our e-commerce platform.", status: "won", createdAt: new Date(Date.now() - 604800000).toISOString(), assignedTo: "Alex Johnson" },
  { id: "l5", name: "Ravi Patel", email: "ravi@tradingfirm.in", phone: "+91 98765 43210", company: "Alpha Trading", service: "Trading System", message: "Need a backtesting platform for our quant strategies.", status: "lost", createdAt: new Date(Date.now() - 1209600000).toISOString(), assignedTo: "Marcus Williams" },
  { id: "l6", name: "Ngozi Eze", email: "ngozi@healthtech.ng", phone: "+234 802 345 6789", company: "HealthTech NG", service: "Web & Mobile App", message: "Patient management app for our chain of clinics across Nigeria.", status: "new", createdAt: new Date(Date.now() - 7200000).toISOString(), assignedTo: null },
];

export const mockLeadsFunnelData = {
  stages: [
    {
      id: "new",
      label: "New",
      count: 38,
      value: 284000,
      avgDays: 0,
      color: "#00D4FF",
      icon: "ri-user-add-line",
      description: "Leads just entered the pipeline",
    },
    {
      id: "contacted",
      label: "Contacted",
      count: 27,
      value: 201500,
      avgDays: 2.4,
      color: "#A78BFA",
      icon: "ri-mail-send-line",
      description: "Initial outreach made",
    },
    {
      id: "proposal",
      label: "Proposal",
      count: 16,
      value: 148000,
      avgDays: 5.1,
      color: "#F59E0B",
      icon: "ri-file-text-line",
      description: "Proposal sent to prospect",
    },
    {
      id: "won",
      label: "Won",
      count: 9,
      value: 94500,
      avgDays: 12.3,
      color: "#00FF88",
      icon: "ri-trophy-line",
      description: "Deal closed successfully",
    },
    {
      id: "lost",
      label: "Lost",
      count: 7,
      value: 53500,
      avgDays: 9.8,
      color: "#FF4D4D",
      icon: "ri-close-circle-line",
      description: "Lead did not convert",
    },
  ],
  monthly: [
    { month: "Jan", new: 12, contacted: 9, proposal: 5, won: 3, lost: 2 },
    { month: "Feb", new: 15, contacted: 11, proposal: 7, won: 4, lost: 3 },
    { month: "Mar", new: 10, contacted: 8, proposal: 4, won: 2, lost: 2 },
    { month: "Apr", new: 18, contacted: 13, proposal: 8, won: 5, lost: 3 },
    { month: "May", new: 14, contacted: 10, proposal: 6, won: 4, lost: 2 },
    { month: "Jun", new: 22, contacted: 16, proposal: 9, won: 6, lost: 3 },
    { month: "Jul", new: 19, contacted: 14, proposal: 8, won: 5, lost: 3 },
    { month: "Aug", new: 25, contacted: 18, proposal: 11, won: 7, lost: 4 },
    { month: "Sep", new: 17, contacted: 13, proposal: 7, won: 4, lost: 3 },
    { month: "Oct", new: 28, contacted: 21, proposal: 13, won: 8, lost: 5 },
    { month: "Nov", new: 23, contacted: 17, proposal: 10, won: 6, lost: 4 },
    { month: "Dec", new: 31, contacted: 24, proposal: 15, won: 10, lost: 5 },
  ],
  conversionRates: {
    newToContacted: 71,
    contactedToProposal: 59,
    proposalToWon: 56,
    overallWinRate: 24,
  },
  pipelineTrend: [
    { month: "Jan", totalValue: 198000, wonValue: 42000, proposalValue: 68000 },
    { month: "Feb", totalValue: 224000, wonValue: 51000, proposalValue: 79000 },
    { month: "Mar", totalValue: 187000, wonValue: 38000, proposalValue: 61000 },
    { month: "Apr", totalValue: 251000, wonValue: 64000, proposalValue: 92000 },
    { month: "May", totalValue: 238000, wonValue: 58000, proposalValue: 84000 },
    { month: "Jun", totalValue: 312000, wonValue: 82000, proposalValue: 108000 },
    { month: "Jul", totalValue: 289000, wonValue: 74000, proposalValue: 97000 },
    { month: "Aug", totalValue: 341000, wonValue: 91000, proposalValue: 118000 },
    { month: "Sep", totalValue: 276000, wonValue: 67000, proposalValue: 94000 },
    { month: "Oct", totalValue: 398000, wonValue: 108000, proposalValue: 142000 },
    { month: "Nov", totalValue: 362000, wonValue: 96000, proposalValue: 128000 },
    { month: "Dec", totalValue: 447000, wonValue: 124000, proposalValue: 158000 },
  ],
};

export const mockTeam = [
  { id: "tm1", name: "Alex Johnson", email: "admin@schemackozo.com", role: "admin", title: "CTO & Lead Engineer", projects: 8, joinedAt: "2019-03-01", status: "active" },
  { id: "tm2", name: "Sarah Chen", email: "sarah@schemackozo.com", role: "staff", title: "Project Manager", projects: 12, joinedAt: "2020-06-15", status: "active" },
  { id: "tm3", name: "Marcus Williams", email: "staff@schemackozo.com", role: "staff", title: "Senior Backend Engineer", projects: 6, joinedAt: "2021-01-10", status: "active" },
  { id: "tm4", name: "Amara Diallo", email: "amara@schemackozo.com", role: "staff", title: "UI/UX Designer", projects: 9, joinedAt: "2021-08-20", status: "active" },
  { id: "tm5", name: "Kwame Asante", email: "kwame@schemackozo.com", role: "staff", title: "DevOps Engineer", projects: 5, joinedAt: "2022-03-05", status: "active" },
  { id: "tm6", name: "Priya Sharma", email: "priya@schemackozo.com", role: "staff", title: "AI/ML Engineer", projects: 4, joinedAt: "2023-01-15", status: "active" },
];
